"use client";

import { Button } from "@/components/atoms/Button";
import WhatMakesCourseDifferentCard from "@/components/molecules/WhatMakesCourseDifferentCard";
import Carousel9 from "@/components/organisms/carousels/Carousel9";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  cardClick,
  ctaClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

// Derives a soft drop-shadow colour from the CTA button's own background colour
// (hex or rgb/rgba) at the given opacity — so the shadow always matches the button.
const shadowFromColor = (color: string, alpha = 0.5): string => {
  if (!color) return "transparent";
  const c = color.trim();
  if (c.startsWith("#")) {
    let hex = c.slice(1);
    if (hex.length === 3) hex = hex.split("").map((ch) => ch + ch).join("");
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return c;
  }
  const m = c.match(/^rgba?\(([^)]+)\)/i);
  if (m) {
    const [r, g, b] = m[1].split(",").map((p) => p.trim());
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return c;
};

interface CardMetadata {
  id: string;
  priority: number;
  card_color: string;
  card_image: string;
  card_image_alt?: string;
  card_title: string;
  card_description: string;
  card_title_text_color: string;
  card_description_text_color: string;
}

interface WhatMakesCourseDifferent1Props {
  data: {
    data: {
      title: string;
      title_text_color: string;
      sub_title: string;
      sub_title_text_color: string;
      point_1: string;
      point_1_text_color: string;
      point_1_icon: string;
      point_2: string;
      point_2_text_color: string;
      point_2_icon: string;
      cta_text: string;
      cta_text_color: string;
      cta_background_color: string;
      cta_icon: string;
      background_color: string;
      carousel_button_color?: string;
      metadata: CardMetadata[];
    };
  };
}

const WhatMakesCourseDifferent1 = ({ data }: any) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);

  const {
    title = "",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    point_1 = "",
    point_1_text_color = "#000000",
    point_1_icon = "",
    point_1_icon_alt = "",
    point_2 = "",
    point_2_text_color = "#000000",
    point_2_icon = "",
    point_2_icon_alt = "",
    cta_text = "",
    cta_text_color = "#FFFFFF",
    cta_background_color = "#000000",
    cta_icon = "",
    cta_icon_alt = "",
    cta_shadow_enabled = false,
    background_color = "#FFFFFF",
    carousel_button_color = "#f74958",
    metadata = [],
  } = data?.data || {};

  // Optional CTA drop shadow — colour derived from the button's own background.
  const ctaShadow =
    cta_shadow_enabled && cta_background_color
      ? `0px 10px 10px ${shadowFromColor(cta_background_color, 0.5)}`
      : undefined;

  // Sort metadata by priority
  const sortedMetadata = [...metadata].sort(
    (a, b) => (a.priority || 0) - (b.priority || 0),
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // Track widget viewed
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "what_makes_course_different_section");
  //   }
  // }, []);

  // Handle card click with tracking
  const handleCardClick = useCallback(
    (cardTitle: string) => {
      cardClick({
        section: "what_makes_course_different_section",
        component: "course_different_card",
        label: cardTitle,
        parent: title,
        redirectLink: "",
        pageKey: "",
      });
      dispatch(
        handleOpenLeaddrop({
          variant: LEAD_DROP_VARIANTS.REGISTER,
        }),
      );
    },
    [dispatch, title],
  );

  // Handle CTA button click with tracking
  const handleOpenLeadForm = useCallback(() => {
    ctaClick({
      section: "what_makes_course_different_section",
      component: "cta_button",
      label: cta_text,
      step: "",
      entity: "register_button",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [dispatch, cta_text]);

  if (isMobile === null) return null;

  if (isMobile) {
    return (
      <section
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-center w-full max-w-none self-stretch overflow-hidden",
          "py-[32px] pl-[20px] gap-6",
        )}
        style={{ backgroundColor: background_color }}
      >
        <div className={cn("flex flex-col w-full", "gap-3")}>
          <h2
            className={cn(
              "text-center break-words",
              "xs:text-heading-6-semibold",
              "line-clamp-3",
            )}
            style={{ color: title_text_color }}
          >
            {title}
          </h2>
          {sub_title && (
            <div
              className={cn(
                "text-center break-words",
                "xs:text-heading-9-regular",
                "line-clamp-4",
              )}
              style={{ color: sub_title_text_color }}
            >
              {sub_title}
            </div>
          )}
        </div>
        <div className={cn("w-full")}>
          <div className="w-full">
            <Carousel9
              slidesPerView={1.2}
              slideGap="18px"
              showDots={true}
              carouselButtonColor={carousel_button_color}
            >
              {sortedMetadata.map((item) => (
                <WhatMakesCourseDifferentCard
                  key={item.id}
                  cardImage={item.card_image}
                  cardImageAlt={item.card_image_alt}
                  cardTitle={item.card_title}
                  cardDescription={item.card_description}
                  cardTitleColor={item.card_title_text_color}
                  cardDescriptionColor={item.card_description_text_color}
                  cardColor={item.card_color}
                  onClick={() => handleCardClick(item.card_title)}
                />
              ))}
            </Carousel9>
          </div>
        </div>
        {cta_text && (
          // Section has pl-[20px] only (right side open for the carousel peek);
          // pr-[20px] here keeps the full-width CTA's side margins symmetric.
          <div className="w-full pr-[20px]">
            <Button
              className={cn(
                // Figma mobile (node 518:4262): full-width maroon CTA, Poppins
                // SemiBold 16px, px-24 / py-16, rounded-8, gap-10, 24px export icon.
                "w-full cursor-pointer flex items-center justify-center",
                "font-poppins font-semibold text-[16px] leading-140",
                "gap-[10px] px-[24px] py-[16px] rounded-[8px]",
              )}
              style={{
                backgroundColor: cta_background_color,
                color: cta_text_color,
                ...(ctaShadow ? { boxShadow: ctaShadow } : {}),
              }}
              onClick={handleOpenLeadForm}
            >
              {cta_text}
              {cta_icon && (
                <div className="relative size-[24px]">
                  <Image className="size-[13px]" src={cta_icon} alt={cta_icon_alt || "CTA"} fill />
                </div>
              )}
            </Button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      ref={ref}
      className="w-full max-w-none self-stretch overflow-hidden"
      style={{ backgroundColor: background_color }}
    >
      <div className={cn("w-full min-w-0", "py-[42px] pl-[80px]")}>
        <div className={cn("flex items-center justify-start gap-[34px]")}>
          <div
            className={cn(
              "flex flex-col shrink-0",
              "sm:gap-11 sm:w-[404px]",
              "md:w-[452px]",
              "lg:w-[608px]",
              "xl:w-[720px]",
            )}
          >
            <div className={cn("flex flex-col", "sm:gap-7")}>
              <div className={cn("flex flex-col", "sm:gap-[18px]")}>
                <h2
                  className={cn(
                    "sm:text-heading-5-semibold",
                    "md:text-heading-4-semibold",
                    "lg:text-new-heading-34-semibold",
                    "xl:text-new-heading-34-semibold",

                    "line-clamp-3 sm:line-clamp-2",
                    "type-h3",
                  )}
                  style={{ color: title_text_color }}
                >
                  {title}
                </h2>
                {sub_title && (
                  <div
                    className={cn(
                      "sm:text-heading-9-regular",
                      "md:text-heading-8-regular",
                      "lg:text-heading-7-regular",
                      "xl:text-heading-6-regular",
                      "line-clamp-4 sm:line-clamp-2",
                      "type-body-sm",
                    )}
                    style={{ color: sub_title_text_color }}
                  >
                    {sub_title}
                  </div>
                )}
              </div>
              <div className={cn("flex flex-col", "sm:gap-6")}>
                {point_1 && (
                  <div className={cn("flex gap-3 items-center justify-start ")}>
                    {point_1_icon && (
                      <Image
                        src={point_1_icon}
                        alt={point_1_icon_alt || "Point 1"}
                        width={24}
                        height={24}
                        className="flex-shrink-0"
                      />
                    )}
                    <div
                      className={cn(
                        "sm:text-heading-9-regular",
                        "md:text-heading-8-regular",
                        "lg:text-heading-7-regular",
                        "xl:text-heading-6-regular",
                        "line-clamp-2",
                      )}
                      style={{ color: point_1_text_color }}
                    >
                      {point_1}
                    </div>
                  </div>
                )}
                {point_2 && (
                  <div className={cn("flex gap-3 items-center justify-start ")}>
                    {point_2_icon && (
                      <Image
                        src={point_2_icon}
                        alt={point_2_icon_alt || "Point 2"}
                        width={24}
                        height={24}
                        className="flex-shrink-0"
                      />
                    )}
                    <div
                      className={cn(
                        "sm:text-heading-9-regular",
                        "md:text-heading-8-regular",
                        "lg:text-heading-7-regular",
                        "xl:text-heading-6-regular",
                        "line-clamp-2",
                      )}
                      style={{ color: point_2_text_color }}
                    >
                      {point_2}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {cta_text && (
              <Button
                className={cn(
                  // Figma compact CTA: flat 16px Poppins SemiBold text, px-24 / py-16, rounded-8,
                  // gap-10 — no per-breakpoint upscaling (previously grew to heading-4 / 28px at xl).
                  "w-fit cursor-pointer flex items-center justify-center",
                  "font-poppins font-semibold text-[16px] leading-[1.4]",
                  "gap-[10px] px-[24px] py-[16px] rounded-[8px]",
                )}
                style={{
                  backgroundColor: cta_background_color,
                  color: cta_text_color,
                  ...(ctaShadow ? { boxShadow: ctaShadow } : {}),
                }}
                onClick={handleOpenLeadForm}
              >
                {cta_text}
                {cta_icon && (
                  <div className="relative size-[24px] shrink-0">
                    <Image className="object-contain" src={cta_icon} alt={cta_icon_alt || "CTA"} fill />
                  </div>
                )}
              </Button>
            )}
          </div>
          <div className={cn("flex-1 min-w-0")}>
            <Carousel9
              slidesPerView={2.2}
              slideGap="18px"
              showDots={sortedMetadata.length > 2}
              carouselButtonColor={carousel_button_color}
            >
              {sortedMetadata.map((item) => (
                <WhatMakesCourseDifferentCard
                  key={item.id}
                  cardImage={item.card_image}
                  cardImageAlt={item.card_image_alt}
                  cardTitle={item.card_title}
                  cardDescription={item.card_description}
                  cardTitleColor={item.card_title_text_color}
                  cardDescriptionColor={item.card_description_text_color}
                  cardColor={item.card_color}
                  onClick={() => handleCardClick(item.card_title)}
                />
              ))}
            </Carousel9>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatMakesCourseDifferent1;
